/**
 * Session-only user impersonation for super admins — actor stays authenticated;
 * effective profile (id, role, name) drives dashboard routing and data queries.
 */

import type { User, UserRole } from '../types/user';
import { resolveEffectiveRole } from './roleViewOverride.js';

export interface ImpersonationTarget {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: UserRole;
  createdAt: string;
  isActive: boolean;
}

export interface UserImpersonationOverride {
  actorUserId: string;
  target: ImpersonationTarget;
  startedAt: string;
  /** Tenant workspace pinned when impersonating from Platform Admin. */
  workspaceOrgId?: string;
  /** Actor's membership org to restore on exit — not a leftover view-as pin. */
  actorHomeOrgId?: string;
  /** Allows platform admins who are not profile super_admin to restore impersonation. */
  actorIsPlatformAdmin?: boolean;
  /** Dashboard role when previewing UI without a real user for that role. */
  viewAsRole?: string;
}

const storageKey = (actorUserId: string) => `impersonation_meta_${actorUserId}`;

export function getImpersonationOverride(actorUserId: string): UserImpersonationOverride | null {
  if (!actorUserId) return null;
  try {
    const raw = localStorage.getItem(storageKey(actorUserId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UserImpersonationOverride;
    if (parsed?.actorUserId && parsed?.target?.id && parsed?.target?.role) {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Session impersonation without importing AuthContext (avoids provider cycles). */
export function getActiveImpersonationOverride(): UserImpersonationOverride | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key?.startsWith('impersonation_meta_')) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as UserImpersonationOverride;
      if (parsed?.actorUserId && parsed?.target?.id && parsed?.target?.role) {
        return parsed;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function getImpersonationWorkspaceOrgId(): string | null {
  return getActiveImpersonationOverride()?.workspaceOrgId?.trim() || null;
}

export function setImpersonationOverride(
  actorUserId: string,
  target: ImpersonationTarget,
  meta?: Pick<
    UserImpersonationOverride,
    'workspaceOrgId' | 'actorHomeOrgId' | 'actorIsPlatformAdmin' | 'viewAsRole'
  >,
): void {
  const payload: UserImpersonationOverride = {
    actorUserId,
    target,
    startedAt: new Date().toISOString(),
    workspaceOrgId: meta?.workspaceOrgId,
    actorHomeOrgId: meta?.actorHomeOrgId,
    actorIsPlatformAdmin: meta?.actorIsPlatformAdmin,
    viewAsRole: meta?.viewAsRole,
  };
  localStorage.setItem(storageKey(actorUserId), JSON.stringify(payload));
}

export function clearImpersonationOverride(actorUserId: string): void {
  if (!actorUserId) return;
  localStorage.removeItem(storageKey(actorUserId));
}

export function hasActiveImpersonation(actorUserId: string): boolean {
  return getImpersonationOverride(actorUserId) != null;
}

export function profileRowToImpersonationTarget(row: {
  id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  role?: string | null;
  created_at?: string | null;
  is_active?: boolean | null;
}): ImpersonationTarget {
  return {
    id: row.id,
    email: row.email || '',
    firstName: row.first_name || '',
    lastName: row.last_name || '',
    phone: row.phone || '',
    role: (row.role || 'customer') as UserRole,
    createdAt: row.created_at || new Date().toISOString(),
    isActive: row.is_active !== false,
  };
}

export function impersonationTargetToUser(target: ImpersonationTarget): User {
  return {
    id: target.id,
    email: target.email,
    firstName: target.firstName,
    lastName: target.lastName,
    phone: target.phone,
    role: target.role,
    createdAt: target.createdAt,
    isActive: target.isActive,
  };
}

/** Apply impersonation (if any) else role-view override on the real profile. */
export function resolveEffectiveIdentity(realUser: User): {
  user: User;
  isImpersonating: boolean;
} {
  const impersonation = getImpersonationOverride(realUser.id);
  const mayImpersonate = realUser.role === 'super_admin' || impersonation?.actorIsPlatformAdmin === true;

  if (impersonation?.target && mayImpersonate) {
    const base = impersonationTargetToUser(impersonation.target);
    const effectiveRole = resolveEffectiveRole(base.id, base.role, base.email) as UserRole;
    return {
      user: { ...base, role: effectiveRole },
      isImpersonating: true,
    };
  }

  if (impersonation && !mayImpersonate) {
    clearImpersonationOverride(realUser.id);
  }

  return {
    user: {
      ...realUser,
      role: resolveEffectiveRole(realUser.id, realUser.role, realUser.email) as UserRole,
    },
    isImpersonating: false,
  };
}
